import React, { useState } from 'react';
import { Alert, Button, Divider, Form, Input, Modal, Space, Typography  } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '@/core/store';
import { LockOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
const { Text, Title  } = Typography;

const passwordRules = [
	{ required: true, message: 'Password is required' },
	{
		min: 10,
		message: 'Password must be at least 10 characters long.',
	},
	{
		pattern: /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?]).*$/,
		message: 'Password must include at least 1 uppercase letter, 1 number, and 1 special character.',
	},
];

const PasswordResetForm = ({ open, handleClose }) => {
	const [ pending, setPending ] = useState(false),
		  [ form ]                = Form.useForm(),
          dispatch                = useDispatch(),
          expiredCode             = useSelector(state => state.auth.expiredCode),
          resetPasswordModal      = useSelector(state => state.auth.loadResetPasswordModal);


	const handleSubmitEmail = async (values) => {
		setPending(true);

		try {
            dispatch(actions.auth.submitEmail(values.email));
			setPending(false);
		} catch(err) {
			setPending(false);
			console.error(err);
		}
	};

    const handleSubmitResetForm = async (values) => {
		setPending(true);

		try {
            dispatch(actions.auth.resetPassword(values.email, values.password, values.code));
			setPending(false);
		} catch(err) {
			setPending(false);
			console.error(err);
		}
	};



	const closeModal = () => {
		if(pending) {
			return false;
		}
        dispatch(actions.auth.setState({loadSubmitEmailModal: false, loadResetPasswordModal: false}));
		form.resetFields();
		handleClose();
	};



	return (
        <>
            <Modal
                title={'Request to reset password'}
                open={open}
                centered
                onCancel={closeModal}
                maskClosable={false}
                footer={null}
                width={420}
            >
                <Space
					direction="vertical"
					size={4}
					style={{
						width: '100%',
						textAlign: 'center',
						marginBottom: 24,
					}}
				>
					<MailOutlined style={{ fontSize: 28 }} />
					<Title level={4} style={{ margin: 0 }}>
						Reset Password
					</Title>
					<Text type="secondary">
						A code will be sent to your email to reset your password.
					</Text>
				</Space>

                <Form
                    className="ts_form_margin"
                    name="submit_email"
                    form={form}
                    onFinish={handleSubmitEmail}
                    requiredMark={false}
                >
                    <Form.Item
                        name="email"
                        rules={[ 
                                { required: true, message: 'Email is required' },
                                { type: 'email', message: 'Please enter a valid email address' },
                            ]}
                    >
                        <Input 
                            className="ts_padding" 
                            disabled={pending} prefix={<MailOutlined />} 
                            placeholder="Email" 
                            autoComplete="email"
							inputMode="email"
                        />
                    </Form.Item>

                    <Divider style={{ margin: '16px 0' }} />

                    <Space style={{ width: '100%' }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            //className="ts_margin_left"
                            size="large"
                            disabled={pending}
                        >
                            Send Code
                        </Button>

                        <Button
                            onClick={closeModal}
                            className="ts_margin_left"
                            size="large"
                            disabled={pending}
                        >
                            Cancel
                        </Button>
                    </Space>
                </Form>
            </Modal>

            { resetPasswordModal && 
                <Modal
                    title={'Reset Password'}
                    open={resetPasswordModal}
                    centered
                    onCancel={closeModal}
                    maskClosable={false}
                    footer={null}
                    width={420}
                >
                    <Space
						direction="vertical"
						size={4}
						style={{
							width: '100%',
							textAlign: 'center',
							marginBottom: 24,
						}}
					>
                        <SafetyOutlined style={{ fontSize: 28 }} />
						<Title level={4} style={{ margin: 0 }}>
							Create New Password
						</Title>
						<Text type="secondary">
							Enter the code from your email and choose a new password.
						</Text>
                    </Space>

                    {expiredCode && (
						<Alert
							type="error"
							showIcon
							message="Reset code expired"
							description="Please request a new code before trying again."
							style={{ marginBottom: 16 }}
						/>
					)}

                    <Form
                        className="ts_form_margin"
                        name="submit_email"
                        layout="vertical"
                        form={form}
                        onFinish={handleSubmitResetForm}
                        requiredMark={false}
                    >
                        <Form.Item
                            label="Email"
                            name="email"
                            rules={[ { required: true, message: 'Email is required' } ]}
                        >
                            <Input
                                disabled={true} 
                                prefix={<MailOutlined />}
								placeholder="Email"
                                size="large"
                            />

                        </Form.Item>

                        <Form.Item
							label="Verification Code"
							name="code"
							rules={[{ required: true, message: 'Code is required' }]}
						>
							<Input
								size="large"
								prefix={<SafetyOutlined />}
								placeholder="Verification Code"
								disabled={pending}
							/>
						</Form.Item>

                        <Form.Item
                            label="New Password"
                            name="password"
                            rules={passwordRules}
                        >
                            <Input.Password
								size="large"
								autoComplete="new-password"
								prefix={<LockOutlined />}
								placeholder="New Password"
								disabled={pending}
							/>
                        </Form.Item>

                        <Form.Item
                            label="Confirm Password"
                            name="confirm_password"
                            dependencies={['password']}
                            rules={[
                                { required: true, message: 'Confirm Password is required' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Passwords do not match!'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
								size="large"
								autoComplete="new-password"
								prefix={<LockOutlined />}
								placeholder="Confirm Password"
								disabled={pending}
							/>
                        </Form.Item>

                        <Divider style={{ margin: '16px 0' }} />

                        <Button
                            type="primary"
                            htmlType="submit"
                            className="ts_margin_left"
                            size="large"
                            loading={pending}
                            disabled={expiredCode}
                        >
                            Submit
                        </Button>

                        <Button
                            onClick={closeModal}
                            className="ts_margin_left"
                            disabled={pending}
                        >
                            Cancel
                        </Button>

                    </Form>
                </Modal>
            }
        </>
	);
};



export default PasswordResetForm;
